import React, { useState, useEffect } from "react";
import api from "../api";
import { Eye, EyeOff } from "lucide-react";

/**
 * Componente: ActualizarEmpleadoAdmin
 * Permite que un administrador modifique los datos de un empleado.
 * - Incluye formato visual para documento, teléfono y salario.
 * - Sincroniza automáticamente el username con el número de documento.
 */
export default function ActualizarEmpleadoAdmin({ empleado, onVolver }) {
  // =========================
  // ESTADOS LOCALES
  // =========================
  const [formData, setFormData] = useState({
    tipo_documento_id: "",
    numero_documento: "",
    nombre: "",
    apellido: "",
    correo: "",
    telefono: "",
    direccion: "",
    departamento_id: "",
    cargo_id: "",
    fecha_nacimiento: "",
    tipo_contrato_id: "",
    salario: "",
    // Contacto de emergencia (se maneja separadamente)
    contacto_nombre: "",
    contacto_apellido: "",
    contacto_telefono: "",
    contacto_parentesco: "",
  });

  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // =========================
  // CARGA INICIAL DE DATOS
  // =========================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await Promise.allSettled([
          api.get('/select-data/tipos-documento'),
          api.get('/select-data/departamentos'),
          api.get('/select-data/roles'),
          api.get('/select-data/cargos'),
          api.get('/select-data/tipos-contrato'),
        ]);

        const extract = (r) => {
          if (!r || r.status !== 'fulfilled' || !r.value) return [];
          const d = r.value.data;
          return Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        };

        const [td, dp, rl, cg, tc] = results.map(extract);

        setTiposDocumento(td);
        setDepartamentos(dp);
        setRoles(rl);
        setCargos(cg);
        setTiposContrato(tc);

        results.forEach((r, idx) => {
          if (r.status === 'rejected') {
            console.error(`select-data request #${idx} failed:`, r.reason);
          }
        });

        // Si hay datos del empleado, precarga el formulario
        if (empleado) {
          console.log("📋 Datos del empleado recibidos:", empleado);

          setFormData({
            tipo_documento_id: empleado.tipo_documento_id || "",
            numero_documento: empleado.numero_documento || "",
            nombre: empleado.nombre || "",
            apellido: empleado.apellido || "",
            correo: empleado.correo || "",
            telefono: empleado.telefono || "",
            direccion: empleado.direccion || "",
            departamento_id: empleado.departamento_id || "",
            cargo_id: empleado.cargo_id || "",
            fecha_nacimiento: empleado.fecha_nacimiento ? empleado.fecha_nacimiento.split('T')[0] : "",
            tipo_contrato_id: empleado.tipo_contrato_id || "",
            salario: empleado.salario || "",
            contacto_nombre: empleado.contactoEmergencia?.nombre || "",
            contacto_apellido: empleado.contactoEmergencia?.apellido || "",
            contacto_telefono: empleado.contactoEmergencia?.telefono || "",
            contacto_parentesco: empleado.contactoEmergencia?.parentesco || "",
          });
        }
      } catch (err) {
        console.error("❌ Error cargando datos iniciales:", err);
        alert("Error cargando datos del empleado o catálogos.");
      }
    };

    fetchData();
  }, [empleado]);

  // =========================
  // MANEJADOR DE CAMBIOS
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Elimina caracteres no numéricos en los campos numéricos
    const cleanValue =
      ["numero_documento", "telefono", "salario"].includes(name)
        ? value.replace(/[^\d]/g, "")
        : value;

    // Si se cambia el número de documento → sincroniza automáticamente el username
    if (name === "numero_documento") {
      setFormData((prev) => ({
        ...prev,
        numero_documento: cleanValue,
        username: cleanValue, // sincroniza automáticamente
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    }
  };

  // =========================
  // ENVÍO DEL FORMULARIO
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      console.log("Enviando datos para actualizar empleado (POST):", formData);
      const res = await api.post(
        `/empleados/${empleado.id_empleado}/actualizar-admin`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Respuesta del servidor:", res.data);
      alert("✅ Empleado actualizado correctamente");
      onVolver();
    } catch (err) {
      console.error("❌ Error al actualizar empleado:", err.response?.data || err);
      alert(err.response?.data?.message || "Error al actualizar empleado");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // FORMATEADORES VISUALES
  // =========================
  const formatDocumento = (value) =>
    value ? value.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "";

  const formatTelefono = (value) =>
    value ? value.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3") : "";

  const formatSalario = (value) =>
    value ? `$ ${Number(value).toLocaleString("es-CO")}` : "";

  // =========================
  // RENDER PRINCIPAL
  // =========================
  if (!empleado) {
    return (
      <div className="p-6 bg-white rounded-2xl shadow-lg">
        <h2 className="text-2xl font-bold mb-4 text-gray-700">
          No se ha seleccionado ningún empleado
        </h2>
        <button
          onClick={onVolver}
          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
        >
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold mb-4">✏️ Actualizar datos del empleado</h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ===================== */}
        {/* DATOS DEL EMPLEADO */}
        {/* ===================== */}
        <div className="md:col-span-2 mb-2 border-b pb-1 font-semibold text-gray-600">
          Datos del empleado
        </div>

        {/* Tipo de documento */}
        <select
          name="tipo_documento_id"
          value={formData.tipo_documento_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar tipo de documento</option>
          {tiposDocumento.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </select>

        {/* Número de documento */}
        <input
          type="text"
          name="numero_documento"
          placeholder="Número de documento"
          value={formatDocumento(formData.numero_documento)}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="nombre"
          placeholder="Nombres"
          value={formData.nombre}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="apellido"
          placeholder="Apellidos"
          value={formData.apellido}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        <input
          type="email"
          name="correo"
          placeholder="Correo electrónico"
          value={formData.correo}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        {/* Teléfono con formato visual */}
        <input
          type="text"
          name="telefono"
          placeholder="Teléfono"
          value={formatTelefono(formData.telefono)}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="direccion"
          placeholder="Dirección"
          value={formData.direccion}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <select
          name="departamento_id"
          value={formData.departamento_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar departamento</option>
          {departamentos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nombre}
            </option>
          ))}
        </select>

        <select
          name="cargo_id"
          value={formData.cargo_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar cargo</option>
          {cargos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>

        {/* Fechas con etiquetas descriptivas */}
        <div>
          <label className="text-sm text-gray-500">Fecha de nacimiento</label>
          <input
            type="date"
            name="fecha_nacimiento"
            value={formData.fecha_nacimiento}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />
        </div>

        <select
          name="tipo_contrato_id"
          value={formData.tipo_contrato_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar tipo de contrato</option>
          {tiposContrato.map((tc) => (
            <option key={tc.id} value={tc.id}>
              {tc.nombre}
            </option>
          ))}
        </select>

        {/* Salario con formato visual */}
        <input
          type="text"
          name="salario"
          placeholder="Salario"
          value={formatSalario(formData.salario)}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        {/* Contacto de emergencia */}
          <div className="md:col-span-2 mt-2 border-b pb-1 font-semibold text-gray-600">
            Contacto de emergencia
          </div>

          <input
            type="text"
            name="contacto_nombre"
            placeholder="Nombre contacto"
            value={formData.contacto_nombre}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />

          <input
            type="text"
            name="contacto_apellido"
            placeholder="Apellido contacto"
            value={formData.contacto_apellido}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />

          <input
            type="text"
            name="contacto_telefono"
            placeholder="Teléfono contacto"
            value={formatTelefono(formData.contacto_telefono)}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />

          <input
            type="text"
            name="contacto_parentesco"
            placeholder="Parentesco"
            value={formData.contacto_parentesco}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />

          {/* BOTONES */}
        <div className="md:col-span-2 flex justify-between mt-4">
          <button
            type="button"
            onClick={onVolver}
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
          >
            ← Volver
          </button>

          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 rounded-lg font-semibold text-white ${
              loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Guardando..." : "Actualizar empleado"}
          </button>
        </div>
      </form>
    </div>
  );
}
